import { collection, doc, setDoc, deleteDoc, query, onSnapshot, getDoc, getDocs, serverTimestamp, Unsubscribe, where } from 'firebase/firestore';
import { db, auth, sanitizeFirestoreData } from './firebase';
import { Order } from '../types';
import { perfTracker } from './perfLogger';
import initialOrdersData from '../data/initialOrders.json';

export const INITIAL_ORDERS: Order[] = (initialOrdersData || []) as unknown as Order[];

const ORDERS_COLLECTION = 'orders';
const MANUAL_PAYMENTS_COLLECTION = 'manual_payment_submissions';

let cachedOrders: Order[] = Array.isArray(INITIAL_ORDERS) && INITIAL_ORDERS.length > 0 ? [...INITIAL_ORDERS] : [];
try {
  const saved = localStorage.getItem('rh_orders_cache');
  if (saved) {
    const parsed = JSON.parse(saved);
    if (Array.isArray(parsed) && parsed.length >= cachedOrders.length) {
      cachedOrders = parsed;
    }
  }
} catch (e) {}


export interface ManualPaymentSubmission {
  id: string; // Document ID (e.g., RH-123456)
  orderId: string;
  customerName: string;
  customerEmail: string;
  gatewayName: string;
  transactionId: string;
  senderAccount: string;
  amount: number;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  createdAt: string;
  receiptImage?: string;
  productDetails: string;
  note?: string;
  rejectionReason?: string;
  updatedAt?: any;
}

const orderSubscribers = new Set<(orders: Order[]) => void>();
let activeOrdersUnsub: Unsubscribe | null = null;

export async function fetchOrdersFromApi(): Promise<Order[]> {
  try {
    const token = await auth.currentUser?.getIdToken();
    const headers: Record<string, string> = {
      'X-Admin-Access': 'true'
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch('/api/admin/orders', { headers });
    if (res.ok) {
      const data = await res.json();
      if (data.success && Array.isArray(data.orders) && data.orders.length > 0) {
        cachedOrders = data.orders;
        try { localStorage.setItem('rh_orders_cache', JSON.stringify(data.orders)); } catch (e) {}
        return data.orders;
      }
    }
  } catch (e) {
    console.warn('fetchOrdersFromApi note:', e);
  }
  return cachedOrders.length > 0 ? cachedOrders : INITIAL_ORDERS;
}

export async function fetchManualPaymentsFromApi(): Promise<ManualPaymentSubmission[]> {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) return [];
    const res = await fetch('/api/admin/manual-payments', {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (res.ok) {
      const data = await res.json();
      if (data.success && Array.isArray(data.submissions)) {
        return data.submissions;
      }
    }
  } catch (e) {
    console.warn('fetchManualPaymentsFromApi note:', e);
  }
  return [];
}

function normalizeOrderDoc(id: string, data: any): Order {
  const mapped = { ...data };
  if (mapped.createdAt && typeof mapped.createdAt.toDate === 'function') {
    mapped.createdAt = mapped.createdAt.toDate().toISOString();
  } else if (mapped.createdAt && typeof mapped.createdAt.seconds === 'number') {
    mapped.createdAt = new Date(mapped.createdAt.seconds * 1000).toISOString();
  }
  if (mapped.updatedAt && typeof mapped.updatedAt.toDate === 'function') {
    mapped.updatedAt = mapped.updatedAt.toDate().toISOString();
  } else if (mapped.updatedAt && typeof mapped.updatedAt.seconds === 'number') {
    mapped.updatedAt = new Date(mapped.updatedAt.seconds * 1000).toISOString();
  }
  if (!mapped.date && mapped.createdAt) {
    mapped.date = mapped.createdAt;
  } else if (mapped.date && typeof mapped.date.toDate === 'function') {
    mapped.date = mapped.date.toDate().toISOString();
  } else if (mapped.date && typeof mapped.date.seconds === 'number') {
    mapped.date = new Date(mapped.date.seconds * 1000).toISOString();
  }
  return { id, ...mapped } as Order;
}

export async function fetchOrdersFromFirestore(): Promise<Order[]> {
  const startTime = performance.now();
  try {
    const q = query(collection(db, ORDERS_COLLECTION));
    const snapshot = await getDocs(q);
    const duration = performance.now() - startTime;
    perfTracker.logFetch(ORDERS_COLLECTION, duration, snapshot.metadata.fromCache ? 'cache' : 'server', snapshot.size);

    const fetched: Order[] = [];
    snapshot.forEach((docSnap) => {
      fetched.push(normalizeOrderDoc(docSnap.id, docSnap.data()));
    });

    if (fetched.length > 0) {
      cachedOrders = fetched;
      try { localStorage.setItem('rh_orders_cache', JSON.stringify(fetched)); } catch (e) {}
      return fetched;
    }

    // Try API fallback
    const apiOrders = await fetchOrdersFromApi();
    if (apiOrders.length > 0) {
      cachedOrders = apiOrders;
      try { localStorage.setItem('rh_orders_cache', JSON.stringify(apiOrders)); } catch (e) {}
      return apiOrders;
    }

    if (cachedOrders && cachedOrders.length > 0) {
      return cachedOrders;
    }

    try {
      const saved = localStorage.getItem('rh_orders_cache');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          cachedOrders = parsed;
          return parsed;
        }
      }
    } catch (e) {}

    return fetched;
  } catch (err) {
    console.warn('Firestore orders fetch note, trying API fallback:', err);
    const apiOrders = await fetchOrdersFromApi();
    if (apiOrders.length > 0) {
      cachedOrders = apiOrders;
      try { localStorage.setItem('rh_orders_cache', JSON.stringify(apiOrders)); } catch (e) {}
      return apiOrders;
    }
    if (cachedOrders && cachedOrders.length > 0) return cachedOrders;
    try {
      const saved = localStorage.getItem('rh_orders_cache');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (e) {}
    return INITIAL_ORDERS || [];
  }
}


export function subscribeToOrdersInFirestore(onOrdersUpdate: (orders: Order[]) => void): () => void {
  // Immediately pass baseline/cached data for 0ms render
  if (cachedOrders && cachedOrders.length > 0) {
    onOrdersUpdate(cachedOrders);
  } else if (INITIAL_ORDERS && INITIAL_ORDERS.length > 0) {
    onOrdersUpdate(INITIAL_ORDERS);
  }

  orderSubscribers.add(onOrdersUpdate);

  // Immediately fetch & pass cached/server data for 0ms render
  fetchOrdersFromFirestore().then((orders) => {
    if (orders && orders.length > 0) {
      cachedOrders = orders;
      try { localStorage.setItem('rh_orders_cache', JSON.stringify(orders)); } catch (e) {}
      orderSubscribers.forEach(cb => {
        try { cb(orders); } catch (e) {}
      });
    }
  });

  // Parallel server API sync (backed by Firebase Admin SDK)
  fetchOrdersFromApi().then((apiOrders) => {
    if (apiOrders && apiOrders.length > 0) {
      cachedOrders = apiOrders;
      try { localStorage.setItem('rh_orders_cache', JSON.stringify(apiOrders)); } catch (e) {}
      orderSubscribers.forEach(cb => {
        try { cb(apiOrders); } catch (e) {}
      });
    }
  });

  if (!activeOrdersUnsub) {
    perfTracker.registerListenerStart(ORDERS_COLLECTION);
    const q = query(collection(db, ORDERS_COLLECTION));

    activeOrdersUnsub = onSnapshot(
      q,
      (snapshot) => {
        const startTime = performance.now();
        const fetched: Order[] = [];
        snapshot.forEach((docSnap) => {
          fetched.push(normalizeOrderDoc(docSnap.id, docSnap.data()));
        });

        const duration = performance.now() - startTime;
        perfTracker.logFetch(ORDERS_COLLECTION, duration, snapshot.metadata.fromCache ? 'cache' : 'snapshot', fetched.length);

        if (fetched.length > 0) {
          cachedOrders = fetched;
          try { localStorage.setItem('rh_orders_cache', JSON.stringify(fetched)); } catch (e) {}
        }

        orderSubscribers.forEach((cb) => {
          try { cb(fetched.length > 0 ? fetched : cachedOrders); } catch (e) {}
        });
      },
      (err) => {
        console.warn('[Firestore Note] Orders snapshot note, syncing via Admin API fallback:', err?.message);
        fetchOrdersFromApi().then((apiOrders) => {
          if (apiOrders && apiOrders.length > 0) {
            cachedOrders = apiOrders;
            try { localStorage.setItem('rh_orders_cache', JSON.stringify(apiOrders)); } catch (e) {}
            orderSubscribers.forEach((cb) => {
              try { cb(apiOrders); } catch (e) {}
            });
          }
        });
      }
    );
  } else {
    perfTracker.logDuplicatePrevented(ORDERS_COLLECTION);
  }

  return () => {
    orderSubscribers.delete(onOrdersUpdate);
    if (orderSubscribers.size === 0 && activeOrdersUnsub) {
      activeOrdersUnsub();
      activeOrdersUnsub = null;
      perfTracker.registerListenerStop(ORDERS_COLLECTION);
    }
  };
}

const manualSubscribers = new Set<(submissions: ManualPaymentSubmission[]) => void>();
let activeManualUnsub: Unsubscribe | null = null;

export function subscribeToManualPaymentsInFirestore(onUpdate: (submissions: ManualPaymentSubmission[]) => void): () => void {
  // Parallel API sync
  fetchManualPaymentsFromApi().then((apiSubs) => {
    if (apiSubs && apiSubs.length > 0) {
      onUpdate(apiSubs);
    }
  });

  if (manualSubscribers.has(onUpdate)) {
    perfTracker.logDuplicatePrevented(MANUAL_PAYMENTS_COLLECTION);
    return () => manualSubscribers.delete(onUpdate);
  }

  manualSubscribers.add(onUpdate);

  if (!activeManualUnsub) {
    perfTracker.registerListenerStart(MANUAL_PAYMENTS_COLLECTION);
    const q = query(collection(db, MANUAL_PAYMENTS_COLLECTION));

    activeManualUnsub = onSnapshot(
      q,
      (snapshot) => {
        const startTime = performance.now();
        const fetched: ManualPaymentSubmission[] = [];
        snapshot.forEach((docSnap) => {
          fetched.push({ id: docSnap.id, ...docSnap.data() } as ManualPaymentSubmission);
        });

        const duration = performance.now() - startTime;
        perfTracker.logFetch(MANUAL_PAYMENTS_COLLECTION, duration, snapshot.metadata.fromCache ? 'cache' : 'snapshot', fetched.length);

        manualSubscribers.forEach((cb) => {
          try { cb(fetched); } catch (e) {}
        });
      },
      (err) => {
        console.warn(`Firestore listener note for '${MANUAL_PAYMENTS_COLLECTION}', using API fallback:`, err?.message);
        fetchManualPaymentsFromApi().then((apiSubs) => {
          if (apiSubs && apiSubs.length > 0) {
            manualSubscribers.forEach((cb) => {
              try { cb(apiSubs); } catch (e) {}
            });
          }
        });
      }
    );
  } else {
    perfTracker.logDuplicatePrevented(MANUAL_PAYMENTS_COLLECTION);
  }

  return () => {
    manualSubscribers.delete(onUpdate);
    if (manualSubscribers.size === 0 && activeManualUnsub) {
      activeManualUnsub();
      activeManualUnsub = null;
      perfTracker.registerListenerStop(MANUAL_PAYMENTS_COLLECTION);
    }
  };
}

export async function saveOrderToFirestore(order: Order): Promise<void> {
  try {
    const payload = sanitizeFirestoreData({
      ...order,
      downloadToken: order.downloadToken ?? "",
      customerNotes: order.customerNotes ?? "",
      updatedAt: serverTimestamp()
    });
    await setDoc(doc(db, ORDERS_COLLECTION, order.id), payload, { merge: true });

    // Automatically ensure a user profile exists in 'users' collection for the customer
    if (order.customerEmail) {
      try {
        const userDocId = order.userId || order.customerEmail.toLowerCase().replace(/[^a-z0-9]/g, '_');
        const userRef = doc(db, 'users', userDocId);
        const userSnap = await getDoc(userRef);
        if (!userSnap.exists()) {
          await setDoc(userRef, {
            uid: userDocId,
            name: order.customerName || order.customerEmail.split('@')[0],
            email: order.customerEmail.toLowerCase(),
            memberSince: new Date().toISOString().split('T')[0],
            role: 'customer',
            status: 'Active',
            totalOrders: 1,
            totalSpent: Number(order.total) || 0,
            lastLogin: serverTimestamp()
          }, { merge: true });
        } else {
          const currentData = userSnap.data();
          await setDoc(userRef, {
            totalOrders: (currentData.totalOrders || 0) + 1,
            totalSpent: (currentData.totalSpent || 0) + (Number(order.total) || 0),
            lastLogin: serverTimestamp()
          }, { merge: true });
        }
      } catch (userErr) {
        console.warn('Background user profile upsert error:', userErr);
      }
    }

    // Grant user access to downloads if payment is complete
    if (order.status === 'Completed' || order.paymentStatus === 'Paid' || order.paymentStatus === 'Approved') {
      if (order.userId) {
        try {
          const { arrayUnion, updateDoc } = require('firebase/firestore');
          const userRef = doc(db, 'users', order.userId);
          const productIds = order.items?.map(i => String((i as any).productId || i.product?.id)).filter(Boolean) || [];
          if (productIds.length > 0) {
            await updateDoc(userRef, {
              purchasedProducts: arrayUnion(...productIds)
            });
          }
        } catch (e) {
          console.warn('Failed to update user purchasedProducts:', e);
        }
      }
    }
  } catch (err) {
    console.error(`Failed to save order #${order.id}:`, err);
    throw err;
  }
}

export async function saveManualPaymentSubmission(submission: ManualPaymentSubmission): Promise<void> {
  try {
    const payload = sanitizeFirestoreData({
      ...submission,
      transactionId: submission.transactionId ?? "",
      senderAccount: submission.senderAccount ?? "",
      receiptImage: submission.receiptImage ?? "",
      note: submission.note ?? "",
      rejectionReason: submission.rejectionReason ?? "",
      updatedAt: serverTimestamp()
    });
    await setDoc(doc(db, MANUAL_PAYMENTS_COLLECTION, submission.orderId), payload, { merge: true });
  } catch (err) {
    console.error(`Failed to write Manual Payment Submission #${submission.orderId}:`, err);
    throw err;
  }
}

export async function updateManualPaymentStatusInFirestore(
  orderId: string,
  status: 'pending' | 'approved' | 'rejected' | 'cancelled',
  rejectionReason?: string
): Promise<void> {
  try {
    const payload = sanitizeFirestoreData({
      status,
      rejectionReason: rejectionReason ?? "",
      updatedAt: serverTimestamp()
    });
    await setDoc(doc(db, MANUAL_PAYMENTS_COLLECTION, orderId), payload, { merge: true });
  } catch (err) {
    console.error(`Failed to update Manual Payment Submission #${orderId} status:`, err);
    throw err;
  }
}

export async function deleteOrderFromFirestore(orderId: string): Promise<void> {
  try {
    await deleteDoc(doc(db, ORDERS_COLLECTION, orderId));
  } catch (err) {
    console.error(`Error deleting order #${orderId}:`, err);
  }
}

export function subscribeToUserOrdersInFirestore(email: string, onOrdersUpdate: (orders: Order[]) => void): () => void {
  const cleanEmail = email.toLowerCase().trim();
  const cacheKey = `rh_user_orders_${cleanEmail}`;

  // 1. 0ms Instant Response from Local Cache
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed) && parsed.length > 0) {
        onOrdersUpdate(parsed);
      }
    }
  } catch (e) {}

  // 2. High-speed Server API Sync (handles auto-linking and permissions)
  let isMounted = true;
  const syncFromApi = async () => {
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) return;

      const res = await fetch('/api/user/my-orders', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success && Array.isArray(data.orders) && isMounted) {
          try { localStorage.setItem(cacheKey, JSON.stringify(data.orders)); } catch (e) {}
          onOrdersUpdate(data.orders);
        }
      }
    } catch (err) {
      console.warn('API my-orders sync note:', err);
    }
  };

  syncFromApi();

  // 3. Real-time Firestore Listener with resilient error catching
  let unsubFirestore: (() => void) | null = null;
  try {
    const q = query(collection(db, ORDERS_COLLECTION), where('customerEmail', '==', cleanEmail));
    unsubFirestore = onSnapshot(
      q,
      (snapshot) => {
        if (!isMounted) return;
        const fetched: Order[] = [];
        snapshot.forEach((docSnap) => {
          fetched.push({ id: docSnap.id, ...docSnap.data() } as Order);
        });
        if (fetched.length > 0) {
          try { localStorage.setItem(cacheKey, JSON.stringify(fetched)); } catch (e) {}
          onOrdersUpdate(fetched);
        }
      },
      (err) => {
        console.warn('Realtime firestore listener note:', err?.message);
      }
    );
  } catch (err) {
    console.warn('Could not bind firestore listener:', err);
  }

  return () => {
    isMounted = false;
    if (unsubFirestore) {
      try { unsubFirestore(); } catch (e) {}
    }
  };
}

export async function getOrderByToken(token: string): Promise<Order | null> {
  try {
    const res = await fetch(`/api/invoices/by-token/${token}`);
    if (res.ok) {
      const data = await res.json();
      if (data && data.success && data.order) {
        return data.order as Order;
      }
    }
  } catch (err) {
    console.error('Error fetching order by token from API', err);
  }

  try {
    const q = query(collection(db, ORDERS_COLLECTION), where('secureToken', '==', token));
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const docData = querySnapshot.docs[0].data();
      return { id: querySnapshot.docs[0].id, ...docData } as Order;
    }
    return null;
  } catch (err) {
    console.error('Error fetching order by token', err);
    return null;
  }
}
